import { C, R, F } from '../../lib/designTokens'
import { useAsyncData } from './useProductKnowledgeData'
import * as pk from '../../services/productKnowledgeService'
import * as pkd from '../../services/productKnowledgeDetailService'
import ReportGapButton from './ReportGapButton'
import { Section, Card, Bi, FieldItem, ActionItem, RuleItem, OutputItem, ExceptionItem, SharedControlItem, Screenshots } from './ProductKnowledgeParts'
import { SCREEN_IMAGES, imageUrl } from './productKnowledgeImages'

// What is on a screen besides plain fields, in reading order, and how rules are grouped by type.
const FIELD_GROUPS = [
  ['tab', 'Tabs / التبويبات'], ['kpi', 'KPIs / المؤشرات'], ['column', 'Columns / الأعمدة'],
  ['toolbar', 'Toolbar / شريط الأدوات'], ['property', 'Properties / الخصائص'], ['module', 'Modules / الوحدات'],
]
const RULE_GROUPS = [
  ['calculation', 'Calculations / المعادلات'], ['alert', 'Alerts / التنبيهات'],
  ['tip', 'Tips / نصائح'], ['negative_knowledge', 'Common mistakes / أخطاء شائعة'], ['faq', 'FAQ / أسئلة شائعة'],
]

const KIND_LABEL = {
  workflow: 'Workflow', screen: 'Screen', output: 'Output', rule: 'Rule',
  exception_case: 'Exception', term: 'Term', area: 'Area', product: 'Product',
}

// Reader for a single Screen unit resolved by key. catalog_get_unit() returns,
// for kind='screen', the authoritative name_en/ar and purpose_en/ar directly
// from catalog.screen. catalog_get_related() returns any catalog.dependency
// rows registered for this unit -- e.g. related Workflows in a different
// Area. catalog_get_screen_detail() adds everything documented about the screen:
// its fields, actions, rules, the workflows that use it, outputs, known
// exceptions and shared controls. Each section is shown only when it has content;
// nothing is fabricated when absent.
export default function ProductKnowledgeUnitReader({ unitKey, onOpenWorkflow, onOpenScreen, onOpenItem }) {
  const { data: unit, loading, error, reload } = useAsyncData(() => pk.getUnit(unitKey), [unitKey])
  const { data: related } = useAsyncData(() => pk.getRelated(unitKey), [unitKey])
  const { data: detail, loading: detailLoading, error: detailError, reload: reloadDetail } = useAsyncData(() => pkd.getScreenDetail(unitKey), [unitKey])

  if (loading) return <div style={{ color: C.sub, fontSize: F.body }}>Loading… / جارٍ التحميل…</div>
  if (error) return (
    <div>
      <p style={{ color: C.red, fontSize: F.body }}>Could not load this content. / تعذّر تحميل هذا المحتوى.</p>
      <button onClick={reload} style={{ fontSize: F.caption, padding: '6px 14px', borderRadius: R.btn, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer' }}>Retry / إعادة المحاولة</button>
    </div>
  )
  if (!unit) return <div style={{ color: C.sub, fontSize: F.body }}>Not found. / غير موجود.</div>

  const hasPurpose = !!(unit.purpose_en || unit.purpose_ar)
  const hasRelated = related && related.length > 0
  const d = detail && detail.found ? detail : null
  const list = (k) => (d && Array.isArray(d[k]) ? d[k] : [])
  const fieldsOf = (kind) => list('fields').filter(f => f.value_kind === kind)
  const plainFields = list('fields').filter(f => !FIELD_GROUPS.some(([k]) => k === f.value_kind))
  const rulesOf = (type) => list('rules').filter(r => r.rule_type === type)
  const plainRules = list('rules').filter(r => !RULE_GROUPS.some(([k]) => k === r.rule_type))

  function openRelated(r) {
    if (r.kind === 'workflow') onOpenWorkflow?.(r.key)
    else onOpenScreen?.(r.key)
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'inline-block', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', padding: '2px 8px', borderRadius: R.tag, background: C.blueBg, color: C.blue, marginBottom: 8 }}>
        Screen / شاشة
      </div>

      <h2 style={{ fontSize: F.heading, fontWeight: 800, color: C.ink, margin: '0 0 4px' }}>
        {unit.name_en || unit.key}
      </h2>
      {unit.name_ar && (
        <h3 style={{ fontSize: F.title, fontWeight: 600, color: C.sub, margin: '0 0 16px' }} dir="rtl">{unit.name_ar}</h3>
      )}

      {hasPurpose ? (
        <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.card, padding: '12px 14px', marginBottom: 20 }}>
          {unit.purpose_en && <div style={{ fontSize: F.bodyLg, color: C.ink2, lineHeight: 1.5 }}>{unit.purpose_en}</div>}
          {unit.purpose_ar && (
            <div style={{ fontSize: F.bodyLg, color: C.ink2, lineHeight: 1.5, marginTop: unit.purpose_en ? 8 : 0 }} dir="rtl">
              {unit.purpose_ar}
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: C.surfaceAlt, border: `1px dashed ${C.border2}`, borderRadius: R.card, padding: '14px', marginBottom: 20, color: C.sub, fontSize: F.body }}>
          This Screen's content has not been documented yet. / لم يُوثَّق محتوى هذه الشاشة بعد.
        </div>
      )}

      {(SCREEN_IMAGES[unitKey] || []).length > 0 && (
        <Section title="Screenshots / لقطات الشاشة" count={SCREEN_IMAGES[unitKey].length}>
          <Screenshots items={SCREEN_IMAGES[unitKey]} imageUrl={imageUrl} />
        </Section>
      )}

      {detailLoading && <div style={{ color: C.sub, fontSize: F.caption, marginBottom: 16 }}>Loading details… / جارٍ تحميل التفاصيل…</div>}
      {detailError && (
        <div style={{ marginBottom: 16, fontSize: F.body, color: C.red }}>
          Could not load this screen's details. / تعذّر تحميل تفاصيل هذه الشاشة.{' '}
          <button onClick={reloadDetail} style={{ fontSize: F.caption, padding: '4px 10px', borderRadius: R.btn, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer' }}>Retry / إعادة المحاولة</button>
        </div>
      )}

      {/* tabs first, then the plain fields, then the other on-screen elements (each in its own section) */}
      {fieldsOf('tab').length > 0 && (
        <Section title="Tabs / التبويبات" count={fieldsOf('tab').length}>
          {fieldsOf('tab').map(f => <FieldItem key={f.key} f={f} hideKind />)}
        </Section>
      )}
      {plainFields.length > 0 && (
        <Section title="Fields / الحقول" count={plainFields.length}>
          {plainFields.map(f => <FieldItem key={f.key} f={f} />)}
        </Section>
      )}
      {FIELD_GROUPS.filter(([k]) => k !== 'tab' && fieldsOf(k).length > 0).map(([k, title]) => (
        <Section key={k} title={title} count={fieldsOf(k).length}>
          {fieldsOf(k).map(f => <FieldItem key={f.key} f={f} hideKind />)}
        </Section>
      ))}
      {list('actions').length > 0 && (
        <Section title="Actions / الإجراءات" count={list('actions').length}>
          {list('actions').map(a => <ActionItem key={a.key} a={a} />)}
        </Section>
      )}
      {plainRules.length > 0 && (
        <Section title="Rules / القواعد" count={plainRules.length}>
          {plainRules.map(r => <RuleItem key={r.key} r={r} onOpen={onOpenItem ? () => onOpenItem('rule', r.key) : undefined} />)}
        </Section>
      )}
      {RULE_GROUPS.filter(([k]) => rulesOf(k).length > 0).map(([k, title]) => (
        <Section key={k} title={title} count={rulesOf(k).length}>
          {rulesOf(k).map(r => <RuleItem key={r.key} r={r} hideType onOpen={onOpenItem ? () => onOpenItem('rule', r.key) : undefined} />)}
        </Section>
      ))}
      {list('workflows').length > 0 && (
        <Section title="Used in workflows / تُستخدم في سير العمل" count={list('workflows').length}>
          {list('workflows').map(w => (
            <Card key={w.key} onClick={onOpenWorkflow ? () => onOpenWorkflow(w.key) : undefined}>
              <div style={{ fontSize: F.bodyLg, fontWeight: 700, color: C.ink }}>{w.name_en}</div>
              {w.name_ar && <div dir="rtl" style={{ fontSize: F.body, color: C.sub }}>{w.name_ar}</div>}
            </Card>
          ))}
        </Section>
      )}
      {list('outputs').length > 0 && (
        <Section title="Outputs / المخرجات" count={list('outputs').length}>
          {list('outputs').map(o => <OutputItem key={o.key} o={o} onOpen={onOpenItem ? () => onOpenItem('output', o.key) : undefined} />)}
        </Section>
      )}
      {list('exceptions').length > 0 && (
        <Section title="Known issues & exceptions / مشاكل معروفة واستثناءات" count={list('exceptions').length}>
          {list('exceptions').map(e => <ExceptionItem key={e.key} e={e} onOpen={onOpenItem ? () => onOpenItem('exception_case', e.key) : undefined} />)}
        </Section>
      )}
      {list('shared_controls').length > 0 && (
        <Section title="Shared controls / عناصر مشتركة" count={list('shared_controls').length}>
          {list('shared_controls').map(s => <SharedControlItem key={s.key} s={s} />)}
        </Section>
      )}

      {hasRelated && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: F.label, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Related / ذو صلة
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {related.map((r, i) => (
              <button key={i} onClick={() => openRelated(r)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', padding: '10px 14px', borderRadius: R.card, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', fontFamily: 'inherit' }}>
                <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', padding: '2px 7px', borderRadius: R.tag, background: C.greenBg, color: C.green, flexShrink: 0 }}>
                  {KIND_LABEL[r.kind] || r.kind}
                </span>
                <span style={{ fontSize: F.bodyLg, color: C.ink }}>{r.label_en || r.key}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <ReportGapButton unit={unit} />
    </div>
  )
}
